import { z } from 'zod';
import { articleUnitSchema } from '../inventory/article.schema';

/**
 * Materialposition eines Projekts (Beschaffungs-Spine, Schritt 6–8 des Projektablaufs).
 * Entsteht aus der Engineering-Stückliste (CSV/Excel-Import) und treibt Reservierung,
 * Bestellung und Wareneingang.
 */

/** Beschaffungsquelle einer Position: bestellen oder aus Lager entnehmen. */
export const materialSourcingSchema = z.enum(['ORDER', 'STOCK']);
export type MaterialSourcing = z.infer<typeof materialSourcingSchema>;

/**
 * Bedarfsliste (Materialliste) eines Projekts — ein Projekt darf mehrere führen
 * (ADR 0080), z. B. „Hauptbedarf" und „Ersatzteile". Die Bezeichnung ist Pflicht
 * und je Projekt eindeutig; `note` sind die Freitext-Infos zu DIESER Liste
 * (früher projektweit an `Project.materialNote`).
 */
export const createMaterialListSchema = z.object({
  name: z.string().trim().min(1, 'Bezeichnung ist erforderlich.').max(120),
  note: z.string().trim().max(4000).nullish(),
});
export type CreateMaterialListInput = z.infer<typeof createMaterialListSchema>;

export const updateMaterialListSchema = z
  .object({
    name: z.string().trim().min(1, 'Bezeichnung ist erforderlich.').max(120),
    note: z.string().trim().max(4000).nullable(),
  })
  .partial();
export type UpdateMaterialListInput = z.infer<typeof updateMaterialListSchema>;

export const materialListResponseSchema = z.object({
  id: z.string().uuid(),
  projectId: z.string().uuid(),
  name: z.string(),
  note: z.string().nullable(),
  /** Anzahl Positionen in dieser Liste (0 = frisch angelegt). */
  itemCount: z.number().int().min(0),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});
export type MaterialListResponse = z.infer<typeof materialListResponseSchema>;

/** Abgeleiteter Deckungsstatus einer Position (vom Server berechnet). */
export const materialCoverageSchema = z.enum([
  'OPEN', // noch nichts veranlasst
  'PARTIAL', // teilweise reserviert/bestellt
  'COVERED', // reserviert + bestellt deckt den Bedarf
  'RECEIVED', // vollständig eingegangen/entnommen
]);
export type MaterialCoverage = z.infer<typeof materialCoverageSchema>;

export const createMaterialItemSchema = z.object({
  /** Bedarfsliste, in die die Position gehört (ADR 0080). */
  listId: z.string().uuid(),
  articleNumber: z.string().trim().min(1).max(100),
  description: z.string().trim().min(1).max(500),
  unit: articleUnitSchema.optional(),
  requiredQty: z.number().positive(),
  note: z.string().trim().max(500).optional(),
  /** Freitext-Baugruppe zur Gliederung (z. B. „Kunststoffkomponenten"). */
  assemblyGroup: z.string().trim().max(200).optional(),
  /**
   * Hinterlegtes Zusatzmaterial des Artikels mit anlegen (VB-26016, ADR 0088).
   * Fehlt die Angabe, entscheidet der Standard am Artikel (`kitDefaultOn`) —
   * so wirkt die Vorbelegung auch für Aufrufer, die nichts davon wissen.
   */
  includeKit: z.boolean().optional(),
});
export type CreateMaterialItemInput = z.infer<typeof createMaterialItemSchema>;

export const updateMaterialItemSchema = z
  .object({
    /**
     * Artikel der Position tauschen (falsch erfasst, Nachfolgeartikel, Freitext
     * gegen echten Artikel). Der Server löst die Nummer gegen den Artikelstamm
     * auf und übernimmt Bezeichnung + Einheit; eine unbekannte Nummer macht die
     * Position zur Freitext-Zeile — genau wie beim Anlegen.
     */
    articleNumber: z.string().trim().min(1).max(100),
    description: z.string().trim().min(1).max(500),
    unit: articleUnitSchema.nullable(),
    requiredQty: z.number().positive(),
    note: z.string().trim().max(500).nullable(),
    /** Explizite Quelle-Wahl; null = dem Vorschlag folgen. */
    sourcing: materialSourcingSchema.nullable(),
    /** Lieferant dieser Position (CRM SUPPLIER); null = Artikel-Standardlieferant nutzen. */
    supplierId: z.string().uuid().nullable(),
    /** Freitext-Baugruppe; null/leer = keine Baugruppe. */
    assemblyGroup: z.string().trim().max(200).nullable(),
  })
  .partial();

/**
 * Lagerbestand reservieren — optional mit Teilmenge (Materialbedarf-Panel).
 * Ohne `quantity` reserviert der Server wie bisher min(frei verfügbar, offen);
 * mit `quantity` exakt diese Menge (Server prüft gegen beide Grenzen).
 */
export const reserveMaterialItemSchema = z.object({
  quantity: z.number().positive().optional(),
});
export type ReserveMaterialItemInput = z.infer<typeof reserveMaterialItemSchema>;

/**
 * Positionen einer Bedarfsliste dauerhaft umsortieren: vollständige Liste der
 * Positions-IDs DIESER Liste in gewünschter Reihenfolge.
 */
export const reorderMaterialItemsSchema = z.object({
  listId: z.string().uuid(),
  orderedIds: z.array(z.string().uuid()).min(1).max(5000),
});
export type ReorderMaterialItemsInput = z.infer<typeof reorderMaterialItemsSchema>;

export type UpdateMaterialItemInput = z.infer<typeof updateMaterialItemSchema>;

/** Eine importierte Zeile (Artikelnummer + Menge), vom Frontend aus CSV/Excel geparst. */
export const importMaterialRowSchema = z.object({
  articleNumber: z.string().trim().min(1).max(100),
  quantity: z.number().positive(),
  /** Optionaler Freitext (Spalte „Bezeichnung/Notiz") — landet als Notiz an der Position. */
  description: z.string().trim().max(500).optional(),
  /** Optionale Baugruppe (Spalte „Baugruppe") — Freitext zur Gliederung. */
  assemblyGroup: z.string().trim().max(200).optional(),
  /** 1-basierte Zeilennummer aus der Datei (für Fehlermeldungen). */
  row: z.number().int().positive().optional(),
});
export type ImportMaterialRow = z.infer<typeof importMaterialRowSchema>;

export const importMaterialItemsSchema = z.object({
  /** Bedarfsliste, in die importiert wird (ADR 0080). */
  listId: z.string().uuid(),
  rows: z.array(importMaterialRowSchema).min(1).max(2000),
  /**
   * Bestehende Positionen DIESER Liste vorher leeren (sonst werden Zeilen
   * angehängt). Andere Listen des Projekts bleiben unberührt.
   */
  replace: z.boolean().default(false),
});
export type ImportMaterialItemsInput = z.infer<typeof importMaterialItemsSchema>;

export const materialItemResponseSchema = z.object({
  id: z.string().uuid(),
  projectId: z.string().uuid(),
  /** Bedarfsliste, zu der die Position gehört (ADR 0080). */
  listId: z.string().uuid(),
  position: z.number().int(),
  articleId: z.string().uuid().nullable(),
  articleNumber: z.string(),
  description: z.string(),
  /** Freitext-Baugruppe zur Gliederung (null = keine). */
  assemblyGroup: z.string().nullable(),
  /**
   * Aus dem Zusatzmaterial eines Artikels entstanden (ADR 0088): Artikelnummer
   * des Hauptartikels; null = normale Position. `kitDepth` ist die Einrücktiefe
   * (1 = direktes Zusatzmaterial). Die Position selbst ist unabhängig.
   */
  kitParentNumber: z.string().nullable(),
  kitDepth: z.number().int().min(0),
  unit: articleUnitSchema.nullable(),
  requiredQty: z.number(),
  reservedQty: z.number(),
  orderedQty: z.number(),
  receivedQty: z.number(),
  /** Offener Beschaffungsbedarf = required − reserved − ordered (≥ 0). */
  openQty: z.number(),
  /** Lagerbestand des verknüpften Artikels (null = freie Position ohne Artikel). */
  stockQty: z.number().nullable(),
  /** Frei verfügbar = stockQty − Σ aktive Reservierungen aller Projekte (null wenn kein Artikel). */
  availableQty: z.number().nullable(),
  coverage: materialCoverageSchema,
  /** Explizit gewählte Quelle (null = Vorschlag folgen). */
  sourcing: materialSourcingSchema.nullable(),
  /** Vorschlag des Systems (Lager wenn verfügbar ≥ offener Bedarf, sonst bestellen). */
  suggestedSourcing: materialSourcingSchema,
  /** Effektive Quelle = sourcing ?? suggestedSourcing. */
  effectiveSourcing: materialSourcingSchema,
  /** An der Position gespeicherte Lieferanten-Wahl (null = Artikel-Standard). */
  supplierId: z.string().uuid().nullable(),
  supplierName: z.string().nullable(),
  /** Standardlieferant des verknüpften Artikels (null = keiner/kein Artikel). */
  defaultSupplierId: z.string().uuid().nullable(),
  defaultSupplierName: z.string().nullable(),
  note: z.string().nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});
export type MaterialItemResponse = z.infer<typeof materialItemResponseSchema>;

/**
 * Fünfstufiger Positions-Status für die Anzeige (feiner als `coverage`, das nur
 * die Deckung kennt). Reihenfolge = Prozessfluss (offen zuerst) und zugleich
 * Sortier-Rang der Status-Spalte.
 *
 * Liegt hier und nicht im Frontend, weil sowohl die Bedarfstabelle als auch die
 * projektübergreifende Übersicht (`/v1/material-demand`) danach zählen — zwei
 * Implementierungen wären zwei Wahrheiten.
 */
export const MATERIAL_STATI = ['OFFEN', 'TEILWEISE', 'BESTELLT', 'GEDECKT', 'GELIEFERT'] as const;
export type MaterialStatus = (typeof MATERIAL_STATI)[number];

/** Mengen, aus denen sich der Positions-Status ableitet. */
export type MaterialStatusQuantities = Pick<
  MaterialItemResponse,
  'requiredQty' | 'reservedQty' | 'orderedQty' | 'receivedQty' | 'openQty'
>;

export function materialStatus(item: MaterialStatusQuantities): MaterialStatus {
  if (item.requiredQty <= 0) return 'OFFEN';
  if (item.receivedQty >= item.requiredQty) return 'GELIEFERT';
  if (item.openQty > 0) return 'OFFEN';
  if (item.receivedQty > 0) return 'TEILWEISE';
  if (item.orderedQty > 0 && item.reservedQty === 0) return 'BESTELLT';
  if (item.reservedQty > 0 && item.orderedQty === 0) return 'GEDECKT';
  return 'TEILWEISE';
}

/** Positions-Zählung je Status — Summe entspricht `itemCount`. */
const demandCountsShape = {
  itemCount: z.number().int().min(0),
  openCount: z.number().int().min(0),
  partialCount: z.number().int().min(0),
  orderedCount: z.number().int().min(0),
  coveredCount: z.number().int().min(0),
  deliveredCount: z.number().int().min(0),
  /** Jüngste Änderung an einer Position (für „zuletzt bearbeitet"-Sortierung). */
  lastChangedAt: z.string().datetime(),
};

/** Eine Bedarfsliste innerhalb der Projektzeile (aufklappbare Unterzeile). */
export const materialDemandListRowSchema = z.object({
  listId: z.string().uuid(),
  listName: z.string(),
  ...demandCountsShape,
});
export type MaterialDemandListRow = z.infer<typeof materialDemandListRowSchema>;

/**
 * Eine Zeile der projektübergreifenden Bedarfsübersicht (Modul Material →
 * „Materialbedarf"). Fasst die Positionen ALLER Bedarfslisten EINES Projekts
 * zusammen; die einzelnen Listen stehen in `lists` und werden in der Tabelle
 * aufgeklappt (ADR 0080). Die Detailliste bleibt
 * `/v1/projects/:id/material-items?listId=…`.
 *
 * Projekte ohne Bedarfspositionen erscheinen NICHT — die Übersicht soll zeigen,
 * wo Material zu disponieren ist, nicht den Projektstamm spiegeln. Eine leere,
 * frisch angelegte Liste erscheint dagegen sehr wohl (sonst wäre sie unauffindbar).
 */
export const materialDemandOverviewRowSchema = z.object({
  projectId: z.string().uuid(),
  /** Projektnummer (`Project.code`) — die Kennung, nach der im Haus gesucht wird. */
  code: z.string(),
  name: z.string(),
  customerName: z.string().nullable(),
  ...demandCountsShape,
  /** Die Bedarfslisten des Projekts (mindestens eine). */
  lists: z.array(materialDemandListRowSchema).min(1),
});
export type MaterialDemandOverviewRow = z.infer<typeof materialDemandOverviewRowSchema>;

/**
 * Ergebnis der Ableitung „Bedarf aus Beleg übernehmen" (#33): wie viele
 * Materialpositionen aus den Auftragspositionen neu angelegt (`created`) bzw. in
 * der Menge aktualisiert wurden (`updated`, Artikel war bereits als Bedarf erfasst)
 * und wie viele Auftragspositionen übersprungen wurden (`skippedFreeText`: Text-/
 * Überschrift-Positionen oder Positionen ohne verknüpften Artikel).
 */
export const deriveMaterialSummarySchema = z.object({
  created: z.number().int().min(0),
  updated: z.number().int().min(0),
  skippedFreeText: z.number().int().min(0),
});
export type DeriveMaterialSummary = z.infer<typeof deriveMaterialSummarySchema>;

/** Ziel-Bedarfsliste der Ableitung „Bedarf aus Beleg übernehmen" (ADR 0080). */
export const deriveMaterialRequestSchema = z.object({ listId: z.string().uuid() });
export type DeriveMaterialRequest = z.infer<typeof deriveMaterialRequestSchema>;

export const deriveMaterialResponseSchema = z.object({
  summary: deriveMaterialSummarySchema,
});
export type DeriveMaterialResponse = z.infer<typeof deriveMaterialResponseSchema>;

/** Eine nicht importierte Zeile, weil die Artikelnummer nicht im Stamm existiert. */
export const importMaterialErrorSchema = z.object({
  row: z.number().int(),
  articleNumber: z.string(),
});
export type ImportMaterialError = z.infer<typeof importMaterialErrorSchema>;

/**
 * Ergebnis eines Imports. Nur Zeilen mit bekanntem Artikel werden angelegt
 * (`imported`); unbekannte Artikelnummern stehen mit Zeilennummer in `errors`.
 */
export const importMaterialResultSchema = z.object({
  imported: z.number().int(),
  matched: z.number().int(),
  unmatched: z.number().int(),
  errors: z.array(importMaterialErrorSchema).default([]),
});
export type ImportMaterialResult = z.infer<typeof importMaterialResultSchema>;
